import { useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from '@/components/ui/dialog'
import ReactMarkdown from 'react-markdown'
import { Field, ErrorMessage, FieldProps } from 'formik'
import { RichTextEditor } from '@/components/Editors/RichTextEditor'
import DOMPurify from 'dompurify'
import { isHtml } from '@/lib/utils'
import { GameroomEvent } from '@/redux/types/room'
import { FileTextIcon } from 'lucide-react'
import { useGamePhase } from '@/hooks/useGamePhase'
import {
  groupEventsByRound,
  getPhaseNumberLabel,
  eventShowsPhaseLabel,
  UNRELEASED_GROUP_KEY,
  type GroupEventsByRoundOptions,
} from '@/utils/groupEventsByRound'

const PREVIEW_CHAR_LIMIT = 250
interface GameRoomDescriptionProps {
  value: string
  onChange?: (value: string) => void
  name: string
  readOnly?: boolean
  placeholder?: string
}

export const GameRoomDescription: React.FC<GameRoomDescriptionProps> = ({
  value,
  onChange,
  name,
  readOnly = false,
  placeholder = 'Enter description with markdown support...',
}) => {
  const handleChange = (newValue: string) => {
    if (onChange) onChange(newValue)
  }

  return (
    <RichTextEditor
      id={name}
      name={name}
      value={value}
      onChange={handleChange}
      placeholder={placeholder}
      disabled={readOnly}
      className='min-h-[200px]'
    />
  )
}

interface GameRoomDescriptionFieldProps {
  name: string
  index?: number
  isMarkdownSupported?: boolean
  readOnly?: boolean
}

export const GameRoomDescriptionField: React.FC<
  GameRoomDescriptionFieldProps
> = ({ name, index, isMarkdownSupported = true, readOnly = false }) => {
  return (
    <div className='space-y-2'>
      <label className='block text-sm font-medium'>
        {index === 0 ? (
          <span className='font-bold text-dark-blue'>
            Day Zero Text/Description
          </span>
        ) : (
          <span className='text-dark-blue'>Event Text</span>
        )}
        {isMarkdownSupported && (
          <span className='ml-1 text-xs text-gray-500'>
            (Markdown supported)
          </span>
        )}
      </label>
      <Field name={name}>
        {({ field, form }: FieldProps) => (
          <GameRoomDescription
            {...field}
            onChange={(value) => form.setFieldValue(name, value)}
            placeholder={
              index === 0 ? 'Enter Day Zero description' : 'Enter event text'
            }
            readOnly={readOnly}
          />
        )}
      </Field>
      <ErrorMessage
        name={name}
        component='div'
        className='mt-1 text-sm text-red-500'
      />
    </div>
  )
}

interface GameRoomDescriptionDisplayProps {
  text: string
  events?: GameroomEvent[]
  currentEventIndex?: number
  showViewAllOnly?: boolean
  eventGroupingOptions?: GroupEventsByRoundOptions
}

export const GameRoomDescriptionDisplay: React.FC<
  GameRoomDescriptionDisplayProps
> = ({
  text,
  events = [],
  currentEventIndex,
  showViewAllOnly = false,
  eventGroupingOptions,
}) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const [isViewAllOpen, setIsViewAllOpen] = useState<boolean>(false)
  const { gameMode } = useGamePhase()

  const hasHtml = text && isHtml(text)
  const hasMultipleEvents = events.length > 1

  const needsTruncation = text && text.length > PREVIEW_CHAR_LIMIT
  const previewText = needsTruncation
    ? `${text.substring(0, PREVIEW_CHAR_LIMIT)}...`
    : text

  const sanitizedPreviewHtml = hasHtml ? DOMPurify.sanitize(previewText) : ''
  const sanitizedFullHtml = hasHtml ? DOMPurify.sanitize(text) : ''

  const renderContent = (content: string) => {
    const contentHasHtml = content && isHtml(content)
    const sanitizedContent = contentHasHtml ? DOMPurify.sanitize(content) : ''

    return contentHasHtml ? (
      <div dangerouslySetInnerHTML={{ __html: sanitizedContent }} />
    ) : (
      <ReactMarkdown
        components={{
          a: ({ href, children }) => (
            <a href={href} target='_blank' rel='noopener noreferrer'>
              {children}
            </a>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    )
  }

  const { unreleased, roundGroups } = useMemo(
    () => groupEventsByRound(events, eventGroupingOptions),
    [events, eventGroupingOptions]
  )

  const renderViewAllEvent = (event: GameroomEvent, idx: number) => {
    const images = event.media?.filter((m) => m.type === 'image') ?? []
    const documents = event.media?.filter((m) => m.type === 'document') ?? []
    const hasMedia = images.length > 0 || documents.length > 0
    const phaseLabel = getPhaseNumberLabel(
      event.released_phase_type,
      gameMode,
      event.released_round_number
    )

    return (
      <div
        key={event.id}
        className='border-b border-gray-200 pb-4 last:border-b-0'
      >
        <div className='flex items-start gap-3 mb-3'>
          <span className='inline-flex items-center justify-center w-8 h-8 bg-dark-blue text-white rounded-full text-sm font-medium flex-shrink-0 mt-1'>
            {idx + 1}
          </span>
          <div className='flex-1'>
            <div className='flex flex-col gap-1'>
              <h3 className='text-lg font-semibold text-dark-blue'>
                {event.title}
              </h3>
              {event.event_datetime && (
                <p className='text-sm text-gray-500 flex flex-wrap items-center gap-1.5'>
                  {new Date(event.event_datetime).toLocaleString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true,
                  })}
                  {eventShowsPhaseLabel(event) && phaseLabel && (
                    <span className='inline-flex items-center rounded-full bg-sky-100 text-sky-800 text-[10px] font-medium px-1.5 py-0.5'>
                      {phaseLabel}
                    </span>
                  )}
                </p>
              )}
            </div>
          </div>
        </div>
        <div className='prose max-w-none ml-11'>
          {renderContent(event.text)}
          {hasMedia && (
            <div className='mt-3 space-y-3'>
              {images.length > 0 && (
                <div className='flex flex-col gap-2'>
                  {images.map((item) => (
                    <img
                      key={item.id}
                      src={item.url}
                      alt='Event media'
                      className='max-w-full rounded border object-contain max-h-48'
                    />
                  ))}
                </div>
              )}
              {documents.length > 0 && (
                <div className='flex flex-wrap gap-2'>
                  {documents.map((doc) => (
                    <a
                      key={doc.id}
                      href={doc.url}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='inline-flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-medium text-dark-blue transition-colors hover:bg-gray-100'
                    >
                      <FileTextIcon className='h-4 w-4 flex-shrink-0' />
                      <span className='truncate'>
                        {doc.caption || 'Document'}
                      </span>
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    )
  }

  const renderGroupedViewAllSections = () => (
    <div className='space-y-8'>
      {unreleased.length > 0 && (
        <div key={UNRELEASED_GROUP_KEY}>
          <h4 className='text-sm font-semibold text-gray-700 mb-4 sticky top-0 bg-white py-1'>
            Unreleased
          </h4>
          <div className='space-y-6 pl-2'>
            {unreleased.map((event) =>
              renderViewAllEvent(event, events.findIndex((e) => e.id === event.id))
            )}
          </div>
        </div>
      )}
      {roundGroups.map((group) => (
        <div key={group.roundKey}>
          <h4 className='text-sm font-semibold text-gray-700 mb-4 sticky top-0 bg-white py-1'>
            {group.roundLabel}
          </h4>
          <div className='space-y-6 pl-2'>
            {group.events.map((event) =>
              renderViewAllEvent(
                event,
                events.findIndex((e) => e.id === event.id)
              )
            )}
          </div>
        </div>
      ))}
    </div>
  )

  if (showViewAllOnly) {
    return (
      <div className='relative'>
        {hasMultipleEvents && (
          <Button
            variant='ghost'
            className='h-9 w-full justify-start px-3 text-sm font-semibold text-slate-700 hover:bg-slate-100'
            onClick={() => setIsViewAllOpen(true)}
          >
            📋 View All Events ({events.length})
          </Button>
        )}

        <Dialog open={isViewAllOpen} onOpenChange={setIsViewAllOpen}>
          <DialogContent className='max-w-4xl max-h-[90vh]'>
            <DialogHeader>
              <DialogTitle>All Game Events</DialogTitle>
              <DialogDescription>
                Showing all {events.length} events grouped by round
              </DialogDescription>
            </DialogHeader>
            <div className='overflow-y-auto max-h-[70vh] pr-2'>
              {renderGroupedViewAllSections()}
            </div>
            <div className='flex justify-between items-center mt-4'>
              <div className='text-sm text-gray-500'>
                Total Events: {events.length}
              </div>
              <DialogClose asChild>
                <Button className='bg-dark-blue hover:bg-dark-blue-hovered'>
                  Close
                </Button>
              </DialogClose>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    )
  }

  return (
    <div className='relative'>
      <div className='prose-sm prose max-w-none'>
        {hasHtml ? (
          <div dangerouslySetInnerHTML={{ __html: sanitizedPreviewHtml }} />
        ) : (
          <ReactMarkdown
            components={{
              a: ({ href, children }) => (
                <a href={href} target='_blank' rel='noopener noreferrer'>
                  {children}
                </a>
              ),
            }}
          >
            {previewText}
          </ReactMarkdown>
        )}
      </div>

      {needsTruncation && (
        <Button
          variant='link'
          className='h-auto p-0 mt-1 text-dark-blue'
          onClick={() => setIsModalOpen(true)}
        >
          Read More
        </Button>
      )}

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className='max-w-3xl max-h-[80vh]'>
          <DialogHeader>
            <DialogTitle>Full Description</DialogTitle>
            <DialogDescription>
              View the complete event description
            </DialogDescription>
          </DialogHeader>
          <div className='overflow-y-auto max-h-[60vh] pr-2'>
            <div className='prose max-w-none'>
              {hasHtml ? (
                <div dangerouslySetInnerHTML={{ __html: sanitizedFullHtml }} />
              ) : (
                <ReactMarkdown
                  components={{
                    a: ({ href, children }) => (
                      <a href={href} target='_blank' rel='noopener noreferrer'>
                        {children}
                      </a>
                    ),
                  }}
                >
                  {text}
                </ReactMarkdown>
              )}
            </div>
            {(() => {
              const currentEvent =
                events.length > 0 && typeof currentEventIndex === 'number'
                  ? events[currentEventIndex]
                  : undefined

              if (!currentEvent?.media || currentEvent.media.length === 0) {
                return null
              }

              const images = currentEvent.media.filter((m) => m.type === 'image')
              const documents = currentEvent.media.filter((m) => m.type === 'document')

              if (images.length === 0 && documents.length === 0) return null

              return (
                <div className='mt-4 space-y-4'>
                  {images.length > 0 && (
                    <div className='flex flex-col gap-2'>
                      {images.map((item) => (
                        <img
                          key={item.id}
                          src={item.url}
                          alt='Event media'
                          className='max-w-full rounded border object-contain max-h-72'
                        />
                      ))}
                    </div>
                  )}
                  {documents.length > 0 && (
                    <div className='flex flex-col gap-2'>
                      {documents.map((doc) => (
                        <a
                          key={doc.id}
                          href={doc.url}
                          target='_blank'
                          rel='noopener noreferrer'
                          className='inline-flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-medium text-dark-blue transition-colors hover:bg-gray-100'
                        >
                          <FileTextIcon className='h-4 w-4 flex-shrink-0' />
                          <span className='truncate'>{doc.caption || 'Document'}</span>
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              )
            })()}
          </div>
          <DialogClose asChild>
            <Button className='mt-4 bg-dark-blue hover:bg-dark-blue-hovered'>
              Close
            </Button>
          </DialogClose>
        </DialogContent>
      </Dialog>

      {hasMultipleEvents && (
        <Dialog open={isViewAllOpen} onOpenChange={setIsViewAllOpen}>
          <DialogContent className='max-w-4xl max-h-[90vh]'>
            <DialogHeader>
              <DialogTitle>All Game Events</DialogTitle>
              <DialogDescription>
                Showing all {events.length} events
                {eventGroupingOptions?.includeUnreleased
                  ? ' grouped by round'
                  : ' in chronological order'}
              </DialogDescription>
            </DialogHeader>
            <div className='overflow-y-auto max-h-[70vh] pr-2'>
              {eventGroupingOptions?.includeUnreleased ? (
                renderGroupedViewAllSections()
              ) : (
              <div className='space-y-6'>
                {events.map((event, idx) => {
                  const images =
                    event.media?.filter((m) => m.type === 'image') ?? []
                  const documents =
                    event.media?.filter((m) => m.type === 'document') ?? []
                  const hasMedia = images.length > 0 || documents.length > 0

                  return (
                    <div
                      key={event.id}
                      className='border-b border-gray-200 pb-4 last:border-b-0'
                    >
                      <div className='flex items-center gap-2 mb-3'>
                        <span className='inline-flex items-center justify-center w-8 h-8 bg-dark-blue text-white rounded-full text-sm font-medium'>
                          {idx + 1}
                        </span>
                        <h3 className='text-lg font-semibold text-dark-blue'>
                          {event.title ? 'Day Zero' : `Event ${idx + 1}`}
                        </h3>
                      </div>
                      <div className='prose max-w-none ml-10'>
                        {renderContent(event.text)}
                        {hasMedia && (
                          <div className='mt-3 space-y-3'>
                            {images.length > 0 && (
                              <div className='flex flex-col gap-2'>
                                {images.map((item) => (
                                  <img
                                    key={item.id}
                                    src={item.url}
                                    alt='Event media'
                                    className='max-w-full rounded border object-contain max-h-48'
                                  />
                                ))}
                              </div>
                            )}
                            {documents.length > 0 && (
                              <div className='flex flex-wrap gap-2'>
                                {documents.map((doc) => (
                                  <a
                                    key={doc.id}
                                    href={doc.url}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className='inline-flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-medium text-dark-blue transition-colors hover:bg-gray-100'
                                  >
                                    <FileTextIcon className='h-4 w-4 flex-shrink-0' />
                                    <span className='truncate'>{doc.caption || 'Document'}</span>
                                  </a>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
              )}
            </div>
            <div className='flex justify-between items-center mt-4'>
              <div className='text-sm text-gray-500'>
                Total Events: {events.length}
              </div>
              <DialogClose asChild>
                <Button className='bg-dark-blue hover:bg-dark-blue-hovered'>
                  Close
                </Button>
              </DialogClose>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}

interface GameRoomDescriptionModule {
  GameRoomDescription: React.FC<GameRoomDescriptionProps>
  GameRoomDescriptionField: React.FC<GameRoomDescriptionFieldProps>
  GameRoomDescriptionDisplay: React.FC<GameRoomDescriptionDisplayProps>
}

const GameRoomDescriptionModule: GameRoomDescriptionModule = {
  GameRoomDescription,
  GameRoomDescriptionField,
  GameRoomDescriptionDisplay,
}

export default GameRoomDescriptionModule
